import { DatePipe, DecimalPipe, TitleCasePipe } from '@angular/common';
import { Component, computed, effect, inject, input, signal } from '@angular/core';
import {
  CacheService,
  CachedContent,
  OrderResponse,
  SimSubscription,
  SimsService,
  SubscriberService,
  TranslateService,
  esimPlanName,
  generateInvoiceName,
} from '@gb-webapp/gb-app';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, finalize, map, of } from 'rxjs';

@Component({
  selector: 'app-invoice',
  imports: [TitleCasePipe, DecimalPipe, DatePipe, TranslatePipe],
  templateUrl: './invoice.component.html',
  styleUrl: './invoice.component.scss',
})
export class InvoiceComponent {
  public orderDetails = input<OrderResponse | null>(null);

  public plan = input<SimSubscription | null>(null);

  public simsService = inject(SimsService);

  public cacheService = inject(CacheService);

  private readonly translateService = inject(TranslateService);

  private readonly subscriberService = inject(SubscriberService);

  public isLoading = input<boolean>(false);

  public translatedPlanName = signal<string>('');

  private readonly rawPlanName = computed(() => {
    const order = this.orderDetails();
    const planValue = this.plan();
    if (order?.name) return order.name;
    if (planValue) return esimPlanName(planValue);
    return '';
  });

  constructor() {
    effect(() => {
      const name = this.rawPlanName();
      if (!name) {
        this.translatedPlanName.set('');
        return;
      }
      const lang = this.subscriberService.subscriberLanguage().code.toLowerCase();
      if (lang === 'en') {
        this.translatedPlanName.set(name);
        return;
      }
      this.translatedPlanName.set('');
      this.translateService
        .translate(name)
        .then((translated) => this.translatedPlanName.set(translated || name))
        .catch(() => this.translatedPlanName.set(name));
    });
  }

  public customerDetails = computed(() => this.orderDetails()?.subscriber);

  public isSubmitting = signal(false);

  public billFrom = computed(() => {
    const billingAddress = this.orderDetails()?.billingAddress;
    if (!billingAddress) {
      return null;
    }

    return {
      ...billingAddress,
      name: billingAddress.name?.trim() || '',
      addressLine: billingAddress.addressLine?.trim() || '',
      landmark: billingAddress.landmark?.trim() || '',
      city: billingAddress.city?.trim() || '',
      state: billingAddress.state?.trim() || '',
      country: billingAddress.country?.trim() || '',
      pincode: billingAddress.pincode?.trim() || '',
    };
  });

  public billingContact = computed(() => {
    const billingContact = this.orderDetails()?.contactDetails;

    if (!billingContact) {
      return null;
    }

    return {
      ...billingContact,
    };
  });

  public downloadInvoice(): Observable<boolean> {
    if (this.isSubmitting()) {
      return of(false);
    }

    const invoiceName = generateInvoiceName(
      this.plan()?.name ?? '',
      (this.cacheService.cachedContent() as CachedContent).cache.config.webAppConfig.name
    );

    this.isSubmitting.set(true);
    return this.simsService
      .downloadInvoice(this.plan()?._id ?? '', invoiceName)
      .pipe(map(() => true))
      .pipe(finalize(() => this.isSubmitting.set(false)));
  }
}
